/**
 * The diary — a second surface, deliberately not the chat.
 *
 * The difference is the whole feature. In the chat someone is talking to a
 * coach: what they say is a commitment, and Compass answers, asks, and builds
 * a map from it. Here someone is writing their day for themselves. Compass
 * reads it, says a few words, and asks nothing. What it LEARNS goes quietly
 * into the background — the people and places in the entry are remembered the
 * same evening, while anything that looks like a commitment waits until they
 * write it again on another day. So a passing Tuesday never becomes a task on
 * their Life Map, and a pattern they keep circling does.
 *
 * Two things this screen owes the writer, and both shape the code below:
 *   1. It must never lose an entry. The text stays in the box until the server
 *      confirms it landed, and a draft survives a reload (localStorage).
 *   2. It must not look like a to-do inbox. No item chips, no counts, no
 *      "3 things extracted" — that is the chat's job and showing it here would
 *      turn a diary into homework.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { BookOpen, CalendarClock, Loader2, RefreshCw, X } from 'lucide-react'
import { Button } from '../components/ui/Button'
import { Card, CardContent } from '../components/ui/Card'
import { DateStamp } from '../components/diary'
import {
  LangPicker, LiveTranscript, MicButton, MicTrouble, useDictationInto,
} from '../components/dictation/Dictation'
import {
  hortonV3Service,
  type DiaryEntry,
} from '../services/hortonV3Service'
import { BRAND } from '../config/brand'

const DRAFT_KEY = 'horton.diary.draft'
const LANG_KEY = 'horton.diary.dictationLang'

/** Now, as a `datetime-local` value: the writer's wall clock, no zone. */
function localNow(): string {
  const d = new Date()
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset())
  return d.toISOString().slice(0, 16)
}

const PLACEHOLDER =
  'How was today?\n\nWho you saw, where you went, what stayed with you. ' +
  'Write it the way you would say it — or press Speak and just say it.'

/** "Today", "Yesterday", or the date — the way a person refers to their own days. */
function dayLabel(iso: string): string {
  const d = new Date(iso)
  const midnight = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime()
  const days = Math.round((midnight(new Date()) - midnight(d)) / 86_400_000)
  if (days === 0) return 'Today'
  if (days === 1) return 'Yesterday'
  return d.toLocaleDateString(undefined, {
    weekday: 'long', month: 'long', day: 'numeric',
    ...(d.getFullYear() === new Date().getFullYear() ? {} : { year: 'numeric' }),
  })
}

function timeLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
}

/** One past entry: their words, then what Compass said, quietly, underneath. */
function Entry({ entry }: { entry: DiaryEntry }) {
  return (
    <Card variant="journal" className="overflow-hidden">
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-baseline gap-2 mb-2">
          <span className="text-sm font-semibold text-primary">{dayLabel(entry.ts)}</span>
          <span className="text-xs text-muted">{timeLabel(entry.ts)}</span>
        </div>
        {/* Their words, in their handwriting (diary-handwritten is inert
            outside diary mode). The assistant's reply stays quiet serif below. */}
        <p className="text-sm text-secondary whitespace-pre-wrap leading-relaxed diary-handwritten diary-entry-text">
          {entry.entry}
        </p>
        {entry.acknowledgement && (
          <p className="mt-3 pt-3 border-t border-soft text-sm text-muted italic leading-relaxed">
            {entry.acknowledgement}
          </p>
        )}
      </CardContent>
    </Card>
  )
}

export default function Diary() {
  const [text, setText] = useState<string>(() => localStorage.getItem(DRAFT_KEY) ?? '')
  const [entries, setEntries] = useState<DiaryEntry[]>([])
  const [nextBefore, setNextBefore] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [available, setAvailable] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // The reply to what they just wrote, shown once, above their entry — so the
  // page does not silently swallow the thing it exists to answer.
  const [justSaid, setJustSaid] = useState<string | null>(null)
  const box = useRef<HTMLTextAreaElement | null>(null)
  // The day this entry is ABOUT. Empty means now, which is the ordinary case;
  // set it to write up last night, or to catch up on a week nobody had time
  // to write. Not decoration — the engine counts DAYS when deciding whether
  // something has recurred, so the wrong day quietly changes what reaches the
  // map.
  const [about, setAbout] = useState<string>('')

  // ── speaking it instead of typing it ──────────────────────────────────
  // A diary is the one screen where talking beats typing: it is long, it is
  // unedited, and it is usually written at the end of a day, on a phone.
  const dictation = useDictationInto(setText, LANG_KEY)

  const load = useCallback(async () => {
    setLoading(true)
    const page = await hortonV3Service.getDiary(20)
    setEntries(page.entries)
    setNextBefore(page.next_before_id ?? null)
    setAvailable(page.available || page.entries.length > 0)
    setLoading(false)
  }, [])

  useEffect(() => { void load() }, [load])
  useEffect(() => { box.current?.focus() }, [])

  // A draft is someone's unsent writing. Keep it across a reload, a closed
  // tab, a dropped connection — anything short of them sending it.
  useEffect(() => {
    if (text) localStorage.setItem(DRAFT_KEY, text)
    else localStorage.removeItem(DRAFT_KEY)
  }, [text])

  const loadMore = useCallback(async () => {
    if (!nextBefore) return
    const page = await hortonV3Service.getDiary(20, nextBefore)
    setEntries((prev) => [...prev, ...page.entries])
    setNextBefore(page.next_before_id ?? null)
  }, [nextBefore])

  const save = useCallback(async () => {
    const entry = text.trim()
    if (!entry || saving) return
    // Whatever is still being said belongs to the entry they are sending, not
    // to the next one: close the mic before the write, not after it.
    if (dictation.isListening) dictation.stop()
    setSaving(true)
    setError(null)
    setJustSaid(null)
    // `datetime-local` carries no zone: it is the writer's wall clock, and
    // `new Date(...)` reads it in their zone, which is exactly right.
    const when = about ? new Date(about) : undefined
    if (when && Number.isNaN(when.getTime())) {
      setSaving(false)
      setError('That date did not make sense.')
      return
    }
    const res = await hortonV3Service.writeDiary(entry, when)
    setSaving(false)
    if (!res.ok) {
      // The text stays exactly where it is. Losing someone's page because a
      // relay hiccuped is the one unforgivable failure on this screen.
      setError(res.error ?? `That did not reach ${BRAND.assistant}. Your writing is still here — try again.`)
      return
    }
    setText('')
    setAbout('')
    setJustSaid(res.acknowledgement || null)
    void load()
  }, [text, saving, load, dictation])

  // Cmd/Ctrl+Enter sends, the way every other compose box in the world does.
  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); void save() }
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
      <div className="flex items-center gap-2">
        <BookOpen className="w-5 h-5 text-brand" />
        <h1 className="text-xl font-semibold text-primary">Diary</h1>
        <DateStamp className="ml-1" />
        <button
          onClick={() => void load()}
          className="ml-auto p-1.5 rounded hover:bg-surface-alt text-muted"
          aria-label="Refresh diary" title="Refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <p className="text-sm text-muted leading-relaxed">
        Write your day. This isn’t the chat — {BRAND.assistant} reads it, says a few words,
        and won’t ask you anything. It remembers the people and places you
        mention, and only puts something on your Life Map if you keep coming
        back to it.
      </p>

      <div className="space-y-2">
        <textarea
          ref={box}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          rows={8}
          placeholder={PLACEHOLDER}
          disabled={saving}
          className="w-full rounded border border-soft bg-surface p-3 text-sm text-primary
                     leading-relaxed placeholder:text-muted focus:outline-none
                     focus:ring-1 focus:ring-brand disabled:opacity-60 resize-y
                     diary-handwritten diary-entry-text"
        />
        <LiveTranscript dictation={dictation} />

        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={() => void save()} disabled={!text.trim() || saving}>
            {saving ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Writing it down…</>
            ) : 'Write it down'}
          </Button>
          <MicButton dictation={dictation} disabled={saving}
                     onToggle={() => box.current?.focus()} />
          <LangPicker dictation={dictation} disabled={saving} />
          {/* Writing about an earlier day. Behind one click, because the
              ordinary case is now and a date box on every entry would make a
              diary feel like a form. */}
          {about === '' ? (
            <button onClick={() => setAbout(localNow())} disabled={saving}
                    className="inline-flex items-center gap-1 text-xs text-muted
                               hover:text-brand disabled:opacity-50"
                    title="File this entry under an earlier day">
              <CalendarClock className="w-3.5 h-3.5" /> Another day
            </button>
          ) : (
            <span className="inline-flex items-center gap-1">
              <input type="datetime-local" value={about} disabled={saving}
                     max={localNow()}
                     onChange={(e) => setAbout(e.target.value)}
                     aria-label="The day and time this entry is about"
                     className="rounded border border-soft bg-surface px-2 py-1
                                text-xs text-secondary disabled:opacity-50" />
              <button onClick={() => setAbout('')} disabled={saving}
                      aria-label="Use now instead"
                      className="p-1 rounded hover:bg-surface-alt text-muted">
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          )}

          <span className="text-xs text-muted">
            {/* The shortcut hint is for keyboards; on a phone it is noise. */}
            {saving
              ? `${BRAND.assistant} is reading it.`
              : <span className="hidden sm:inline">⌘/Ctrl + Enter</span>}
          </span>
        </div>
        <MicTrouble dictation={dictation} />
        {error && (
          <div className="text-sm text-red-600 dark:text-red-400">{error}</div>
        )}
      </div>

      {justSaid && (
        <Card variant="note">
          <CardContent className="p-4 text-sm text-secondary italic leading-relaxed">
            {justSaid}
          </CardContent>
        </Card>
      )}

      {!available && !loading && (
        <div className="text-sm text-muted">
          The diary isn’t switched on for this account yet.
        </div>
      )}

      <div className="space-y-3">
        {loading && entries.length === 0 ? (
          <div className="text-sm text-muted flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Opening your diary…
          </div>
        ) : entries.length === 0 ? (
          available && (
            <div className="text-sm text-muted leading-relaxed">
              Nothing written yet. The first entry is the hardest and the shortest —
              a couple of lines about today is plenty.
            </div>
          )
        ) : (
          entries.map((e) => <Entry key={e.turn_id} entry={e} />)
        )}
        {nextBefore && (
          <button
            onClick={() => void loadMore()}
            className="w-full text-xs text-muted hover:text-secondary py-2"
          >
            Earlier entries
          </button>
        )}
      </div>
    </div>
  )
}
